/**
 * Roadmap #16 + #17 — the client doors to the camera registry and coverage.
 *
 * Reads go through RLS (shop members). Every write goes through a server
 * authority (save_shop_camera / archive_shop_camera / set_camera_coverage,
 * shop management only) — the browser cannot write the tables, so it cannot
 * forge health evidence or hard-delete history. One query per table: never
 * coverage camera-by-camera.
 */
import { supabase } from '@/integrations/supabase/client';
import { normalizeSpotRows, rectOf, type FacilityRect, type LayoutRow } from '@/lib/location/facilityLayout';
import type { CameraCoverageRow, CameraPlacement, CoverageTarget, ShopCamera } from './model';

const CAMERA_COLUMNS =
  'id, shop_id, name, placement, observes_occupancy, observes_transition, reads_plates, enabled, archived_at, last_seen_at, last_observation_at, last_error_at, last_error';

interface CameraRow {
  id: string; shop_id: string; name: string; placement: CameraPlacement;
  observes_occupancy: boolean; observes_transition: boolean; reads_plates: boolean;
  enabled: boolean; archived_at: string | null;
  last_seen_at: string | null; last_observation_at: string | null; last_error_at: string | null; last_error: string | null;
}

export function cameraFromRow(r: CameraRow): ShopCamera {
  return {
    id: r.id, shopId: r.shop_id, name: r.name, placement: r.placement,
    observesOccupancy: r.observes_occupancy, observesTransition: r.observes_transition, readsPlates: r.reads_plates,
    enabled: r.enabled, archivedAt: r.archived_at,
    lastSeenAt: r.last_seen_at, lastObservationAt: r.last_observation_at,
    lastErrorAt: r.last_error_at, lastError: r.last_error,
  };
}

/** Every camera of the shop, archived included (shown apart, kept for history). */
export async function readShopCameras(shopId: string): Promise<ShopCamera[]> {
  const { data, error } = await (supabase as any)
    .from('shop_cameras')
    .select(CAMERA_COLUMNS)
    .eq('shop_id', shopId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as CameraRow[]).map(cameraFromRow);
}

/** ACTIVE coverage of every camera of the shop — one query. */
export async function readShopCameraCoverage(shopId: string): Promise<CameraCoverageRow[]> {
  const { data, error } = await (supabase as any)
    .from('camera_coverage')
    .select('id, camera_id, target_kind, bay_id, parking_spot_id, label')
    .eq('shop_id', shopId)
    .is('removed_at', null);
  if (error) throw error;
  return ((data ?? []) as Array<{ id: string; camera_id: string; target_kind: CameraCoverageRow['kind'];
    bay_id: string | null; parking_spot_id: string | null; label: string | null }>)
    .map((r) => ({ id: r.id, cameraId: r.camera_id, kind: r.target_kind, bayId: r.bay_id,
                   parkingSpotId: r.parking_spot_id, label: r.label }));
}

export interface CameraTarget { id: string; label: string; rect: FacilityRect | null }
export interface CameraTargets { bays: CameraTarget[]; spots: CameraTarget[] }

/**
 * The shop's mappable physical targets: every ACTIVE work bay and ACTIVE
 * parking spot, in the shop's own order — with a rectangle where the layout
 * has one (drawn on the map) and listed either way (so a spot without
 * geometry is still mappable, and is never mistaken for a removed one).
 */
export async function readCameraTargets(shopId: string): Promise<CameraTargets> {
  const [baysRes, spotsRes] = await Promise.all([
    supabase.from('shop_bays')
      .select('id, name, bay_number, bay_category, is_active, position_x, position_y, width, height')
      .eq('shop_id', shopId)
      .order('bay_number', { ascending: true }),
    supabase.from('parking_lot_spots')
      .select('id, label, order_index, position_x, position_y, width, height')
      .eq('shop_id', shopId)
      .eq('is_active', true)
      .order('order_index', { ascending: true }),
  ]);
  if (baysRes.error) throw baysRes.error;
  if (spotsRes.error) throw spotsRes.error;
  const bayRows = (baysRes.data ?? []) as Array<LayoutRow & { bay_category: string | null; is_active: boolean | null }>;
  const active = bayRows.filter((b) => b.is_active !== false);
  const work = active.filter((b) => b.bay_category !== 'parking');
  const parkingBays = active.filter((b) => b.bay_category === 'parking');
  const spotRows = (spotsRes.data ?? []) as LayoutRow[];
  const spotRects = new Map(normalizeSpotRows(spotRows, parkingBays).map((r) => [r.id, r]));
  return {
    bays: work.map((b) => {
      const label = b.name || `Bay ${b.bay_number ?? ''}`.trim();
      return { id: b.id, label, rect: rectOf(b, label) };
    }),
    spots: spotRows.map((s) => ({ id: s.id, label: s.label ?? '', rect: spotRects.get(s.id) ?? null })),
  };
}

/** ok + cameraId on success; ok false + reason otherwise. */
export interface CameraActResult { ok: boolean; cameraId?: string; reason: string }

function actResult(data: unknown, error: unknown): CameraActResult {
  if (error) return { ok: false, reason: (error as { message?: string }).message ?? 'Camera change failed' };
  const r = (data ?? {}) as { success?: boolean; reason?: string; camera_id?: string };
  // Unknown = failure: only an explicit success changed anything.
  return r.success === true && r.camera_id
    ? { ok: true, cameraId: r.camera_id, reason: '' }
    : { ok: false, reason: r.reason ?? 'Camera change failed' };
}

export interface CameraDraft {
  name: string;
  placement: CameraPlacement;
  observesOccupancy: boolean;
  observesTransition: boolean;
  readsPlates: boolean;
  enabled: boolean;
}

/** Create (cameraId null) or edit configuration. Never touches health evidence. */
export async function saveShopCamera(shopId: string, cameraId: string | null, d: CameraDraft): Promise<CameraActResult> {
  const { data, error } = await (supabase as any).rpc('save_shop_camera', {
    p_shop_id: shopId,
    p_camera_id: cameraId,
    p_name: d.name,
    p_placement: d.placement,
    p_observes_occupancy: d.observesOccupancy,
    p_observes_transition: d.observesTransition,
    p_reads_plates: d.readsPlates,
    p_enabled: d.enabled,
  });
  return actResult(data, error);
}

export async function archiveShopCamera(cameraId: string): Promise<CameraActResult> {
  const { data, error } = await (supabase as any).rpc('archive_shop_camera', { p_camera_id: cameraId });
  return actResult(data, error);
}

/** The camera's active coverage becomes exactly `targets`. */
export async function setCameraCoverage(cameraId: string, targets: readonly CoverageTarget[]): Promise<CameraActResult> {
  const { data, error } = await (supabase as any).rpc('set_camera_coverage', {
    p_camera_id: cameraId,
    p_targets: targets.map((t) => ({
      kind: t.kind,
      bay_id: t.kind === 'BAY' ? t.bayId : null,
      parking_spot_id: t.kind === 'PARKING_SPOT' ? t.parkingSpotId : null,
      label: t.kind === 'BAY' || t.kind === 'PARKING_SPOT' ? null : (t.label ?? '').trim(),
    })),
  });
  return actResult(data, error);
}

/** Server reason → words a manager can act on. */
export function cameraRefusalMessage(reason: string): string {
  switch (reason) {
    case 'NOT_AUTHORIZED': return 'Only shop managers and owners can configure cameras.';
    case 'INVALID_NAME': return 'Give the camera a name (up to 80 characters).';
    case 'INVALID_PLACEMENT': return 'Choose Interior or Exterior.';
    case 'NO_CAPABILITY': return 'Choose at least one thing this camera can do.';
    case 'CAMERA_ARCHIVED': return 'This camera is archived and can no longer be changed.';
    case 'SHOP_MISMATCH': return 'This camera belongs to a different shop.';
    case 'TARGET_NOT_AVAILABLE': return 'One of the chosen bays or spots is no longer available. Refresh and try again.';
    case 'INVALID_TARGET': case 'INVALID_TARGETS': return 'One of the coverage entries is not valid.';
    default: return reason;
  }
}
